import { Badge, Box, Button, Flex, Heading, Text } from '@chakra-ui/react';
import PartnerAvatar from '@features/supply/PartnerAvatar';
import {
  BusinessPartner,
  getEarliestDeliveryDate,
  getSectorLabel,
} from '@features/supply/fakeSupplyChainData';
import { formatDate } from 'helpers/dates';
import { useHref } from 'hooks/useHref';
import { useRouter } from 'next/navigation';
import { memo, useCallback, useMemo } from 'react';
import { LuX } from 'react-icons/lu';

interface BusinessPartnerCardProps {
  partner: BusinessPartner;
  isExpanded: boolean;
  onSelect: (id: string) => void;
  onClose: () => void;
}

const MAX_VISIBLE_ITEMS = 3;

const HOVER_BORDER = { borderColor: 'border.dark' } as const;
const HOVER_NONE = {} as const;
const FOCUS_VISIBLE_NONE = {} as const;
const HOVER_BG = { bg: 'surface.hover' } as const;
const HOVER_OPACITY = { opacity: 0.9 } as const;
const FOCUS_VISIBLE_STYLE = {
  outline: '2px solid',
  outlineColor: 'border.emphasized',
  outlineOffset: '2px',
} as const;

const BusinessPartnerCard = memo(
  ({ partner, isExpanded, onSelect, onClose }: BusinessPartnerCardProps) => {
    const router = useRouter();
    const { supplyPartnerHref } = useHref();

    const handleClick = useCallback(() => {
      if (isExpanded === false) {
        onSelect(partner.id);
      }
    }, [isExpanded, onSelect, partner.id]);

    const handleSeeDetails = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        router.push(supplyPartnerHref(partner.id));
      },
      [router, supplyPartnerHref, partner.id],
    );

    const handleClose = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onClose();
      },
      [onClose],
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (isExpanded === false) {
            onSelect(partner.id);
          }
        } else if (e.key === 'Escape' && isExpanded) {
          onClose();
        }
      },
      [isExpanded, onSelect, onClose, partner.id],
    );

    const formattedDate = useMemo(() => {
      const earliest = getEarliestDeliveryDate(partner.inventoryNeeds);
      if (earliest == null) {
        return null;
      }
      return formatDate(earliest);
    }, [partner.inventoryNeeds]);

    const { visibleItems, remainingCount } = useMemo(() => {
      return {
        visibleItems: partner.inventoryNeeds.slice(0, MAX_VISIBLE_ITEMS),
        remainingCount: partner.inventoryNeeds.length - MAX_VISIBLE_ITEMS,
      };
    }, [partner.inventoryNeeds]);

    const sectorLabel = useMemo(() => getSectorLabel(partner.sector), [partner.sector]);

    return (
      <Flex
        direction="column"
        bg="surface.muted"
        borderRadius="xl"
        borderColor="border.light"
        borderWidth="1px"
        cursor={isExpanded ? 'default' : 'pointer'}
        _hover={isExpanded ? HOVER_NONE : HOVER_BORDER}
        _focusVisible={isExpanded ? FOCUS_VISIBLE_NONE : FOCUS_VISIBLE_STYLE}
        transition="all 0.3s ease-in-out"
        overflow="hidden"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role={isExpanded ? undefined : 'button'}
        p={0}
      >
        <Box position="relative" overflow="hidden" flex={1} bg="surface.background">
          <Flex
            direction="column"
            p={4}
            gap={3}
            position={isExpanded ? 'absolute' : 'relative'}
            opacity={isExpanded ? 0 : 1}
            transform={isExpanded ? 'translateY(-8px)' : 'translateY(0)'}
            transition="all 0.3s ease-in-out"
            pointerEvents={isExpanded ? 'none' : 'auto'}
            w="full"
          >
            <Flex alignItems="center" gap={3}>
              <PartnerAvatar initials={partner.initials} />
              <Flex direction="column" flex={1} minW={0}>
                <Heading size="sm" truncate title={partner.name}>
                  {partner.name}
                </Heading>
                <Text fontSize="xs" color="text.secondary">
                  {sectorLabel}
                </Text>
              </Flex>
            </Flex>
            <Flex alignItems="center" mt="auto" gap={1}>
              <Text fontSize="lg" fontWeight="semibold">
                {partner.inventoryNeeds.length}
              </Text>
              <Text fontSize="xs">items need supply</Text>
            </Flex>
          </Flex>

          <Flex
            direction="column"
            p={4}
            gap={3}
            position={isExpanded ? 'relative' : 'absolute'}
            top={0}
            left={0}
            opacity={isExpanded ? 1 : 0}
            transform={isExpanded ? 'translateY(0)' : 'translateY(8px)'}
            transition="all 0.3s ease-in-out"
            pointerEvents={isExpanded ? 'auto' : 'none'}
            w="full"
          >
            <Flex alignItems="flex-start" justifyContent="space-between">
              <Flex alignItems="center" gap={3}>
                <PartnerAvatar initials={partner.initials} />
                <Heading size="sm" truncate title={partner.name}>
                  {partner.name}
                </Heading>
              </Flex>
              <Flex
                cursor="pointer"
                p={1}
                borderRadius="md"
                _hover={HOVER_BG}
                onClick={handleClose}
              >
                <LuX size={16} />
              </Flex>
            </Flex>

            <Badge variant="outline" fontSize="2xs" borderRadius="full" px={2} w="fit-content">
              {sectorLabel}
            </Badge>

            <Flex direction="column" gap={1.5}>
              {visibleItems.map((need) => (
                <Flex
                  key={need.id}
                  justifyContent="space-between"
                  alignItems="center"
                  fontSize="xs"
                >
                  <Text color="text.primary">{need.itemName}</Text>
                  <Text color="text.secondary" flexShrink={0} ml={2}>
                    {need.quantity.toLocaleString()} {need.unit}
                  </Text>
                </Flex>
              ))}
              {remainingCount > 0 && (
                <Text fontSize="xs" color="text.secondary">
                  & {remainingCount} more
                </Text>
              )}
            </Flex>

            {formattedDate != null && (
              <Text fontSize="xs" color="text.secondary">
                Earliest delivery: {formattedDate}
              </Text>
            )}

            <Button
              w="full"
              size="sm"
              bg="surface.inverse"
              color="text.inverse"
              borderRadius="lg"
              _hover={HOVER_OPACITY}
              mt="auto"
              onClick={handleSeeDetails}
            >
              See more details
            </Button>
          </Flex>
        </Box>
      </Flex>
    );
  },
);
BusinessPartnerCard.displayName = 'BusinessPartnerCard';

export default BusinessPartnerCard;
